// =============================================================================
// Event detail view — opens when an event in the day list or the activity
// feed is tapped. Shows the event in its label color with participant
// avatars, date/time, its photos, an activity log, and chat-style comments
// (text and photos); edit and delete are reachable from the header. Looks
// the event up across every calendar the user can see and closes itself if
// it was deleted meanwhile.
//
// Layout: the tinted header stays put, only the white part scrolls. On the
// phone the sheet follows the visual viewport, so when the keyboard comes up
// the thread shrinks and the composer sits right above the keys instead of
// the whole page being pushed around.
// =============================================================================

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Pencil, Trash2, ChevronRight, ChevronUp, ChevronDown, Send, ImagePlus, X, Plus, Loader2 } from 'lucide-react';
import { format, parseISO, addDays, differenceInCalendarDays } from 'date-fns';
import { formatDate as fmt } from '../../lib/dates';
import { useTranslation } from '../../lib/i18n';
import { useConfirm } from '../../lib/ConfirmDialog';
import { useAuth } from '../../lib/AuthContext';
import { useToast } from '../../lib/Toast';
import {
  attachmentFolder,
  uploadEventPhotos,
  deleteEventPhotos,
  signEventPhotos,
  eventPhotoPaths
} from '../../lib/eventAttachments';

// Height and offset of the part of the page the user can actually see. iOS
// does not shrink the layout viewport for the keyboard, only this one.
const useVisualViewport = () => {
  const read = () => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!vv) return { height: null, top: 0, keyboard: false };
    // iOS keeps the layout viewport and shrinks only the visual one while
    // the keyboard is up; a big gap between the two means the keyboard.
    const keyboard = vv.height < window.innerHeight * 0.85;
    return { height: vv.height, top: vv.offsetTop, keyboard };
  };
  const [box, setBox] = useState(read);
  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!vv) return undefined;
    const update = () => setBox(read());
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, []);
  return box;
};

const EventDetail = ({
  eventId,
  occurrenceDate,
  events,
  getCategory,
  membersById,
  renderCreatorAvatar,
  onClose,
  onEdit,
  onRequestRecurringDelete,
  commitAllEvents,
  commentDraft,
  setCommentDraft,
  onAddComment,
  onSetAttachments
}) => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const toast = useToast();
  const { user } = useAuth();
  const viewport = useVisualViewport();

  const ev = events.find((e) => e.id === eventId);

  // Photos are private files; show them through short-lived signed URLs,
  // refreshed whenever the set of paths changes.
  const paths = useMemo(() => eventPhotoPaths(ev), [ev]);
  const pathsKey = paths.join('|');
  const [signed, setSigned] = useState({});
  useEffect(() => {
    let alive = true;
    const missing = paths.filter((p) => !signed[p]);
    if (missing.length === 0) return undefined;
    signEventPhotos(missing).then((map) => {
      if (alive && Object.keys(map).length) setSigned((prev) => ({ ...prev, ...map }));
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathsKey]);

  // The header folds to one row while the keyboard is up so the thread
  // keeps its room; the chevrons override that either way, and the
  // override resets whenever the keyboard comes or goes.
  const [manualCollapsed, setManualCollapsed] = useState(null);
  useEffect(() => setManualCollapsed(null), [viewport.keyboard]);
  const collapsed = manualCollapsed ?? viewport.keyboard;

  const [pending, setPending] = useState([]); // photos picked for the next comment
  const [uploading, setUploading] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const [lightbox, setLightbox] = useState(null); // { path, onRemove? }
  const threadRef = useRef(null);
  const inputRef = useRef(null);
  const commentFileRef = useRef(null);
  const eventFileRef = useRef(null);

  // Keep the newest message in view: on open, after sending, and when the
  // keyboard changes how much of the thread is visible.
  const commentCount = (ev?.comments || []).length;
  useLayoutEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [commentCount, viewport.height, eventId]);

  // Object URLs for the picked-but-not-sent photos.
  const previews = useMemo(() => pending.map((f) => ({ file: f, url: URL.createObjectURL(f) })), [pending]);
  useEffect(() => () => previews.forEach((p) => URL.revokeObjectURL(p.url)), [previews]);

  if (!ev) {
    // Event was deleted while open — auto-close.
    setTimeout(() => onClose(), 0);
    return null;
  }
  const cat = getCategory(ev.categoryId);
  const color = cat?.color || '#64748b';
  // Faces belong to participants only; the creator is not implied.
  const ids = Array.isArray(ev.participantIds) ? ev.participantIds : [];
  // A repeating event is stored once, with its first date; show the
  // occurrence that was tapped, shifting start and end by the same amount.
  const shift =
    occurrenceDate && occurrenceDate !== ev.date
      ? differenceInCalendarDays(parseISO(occurrenceDate), parseISO(ev.date))
      : 0;
  const fmtDate = (iso) => fmt(addDays(parseISO(iso), shift), 'weekdayFull');
  const fmtTime = (hhmm) => hhmm || '';
  const sameDay = (ev.endDate || ev.date) === ev.date;
  // One line for the folded header: "Di., 22. Sep. · 18:30–21:30".
  const compactWhen = (() => {
    const day = fmt(addDays(parseISO(ev.date), shift), 'weekdayShort');
    const days = sameDay ? day : `${day} – ${fmt(addDays(parseISO(ev.endDate), shift), 'weekdayShort')}`;
    if (ev.allDay || !ev.startTime) return `${days} · ${t('calendar.allDay')}`;
    return `${days} · ${ev.startTime}${ev.endTime ? `–${ev.endTime}` : ''}`;
  })();
  const folder = attachmentFolder(ev, user?.id);
  const attachments = Array.isArray(ev.attachments) ? ev.attachments : [];

  // Merge audit + comments into one chronological timeline.
  const audit = (ev.audit || []).map((a) => ({ ...a, kind: 'audit' }));
  const comments = (ev.comments || []).map((c) => ({ ...c, kind: 'comment' }));
  const timeline = [...audit, ...comments].sort((a, b) => (a.at || '').localeCompare(b.at || ''));

  const fieldLabel = (f) => {
    switch (f) {
      case 'title':
        return t('common.title');
      case 'date':
        return t('common.date');
      case 'label':
        return t('calendar.fieldLabel');
      case 'notes':
        return t('common.notes');
      case 'participants':
        return t('sharedCalendar.participants');
      case 'recurrence':
        return t('calendar.recurrence');
      default:
        return f;
    }
  };
  const auditSummary = (a) => {
    if (a.type === 'created') return t('calendar.auditCreated');
    const fields = a.fields || [];
    if (fields.length === 0) return t('calendar.auditUpdated');
    return t('calendar.auditFieldsUpdated', { fields: fields.map(fieldLabel).join(', ') });
  };
  const fmtRelative = (iso) => {
    try {
      const d = parseISO(iso);
      const today = new Date();
      const diffDays = Math.floor(
        (today.setHours(0, 0, 0, 0) - new Date(d).setHours(0, 0, 0, 0)) / 86400000
      );
      if (diffDays === 0) return format(d, 'HH:mm');
      if (diffDays === 1) return t('common.yesterday', 'Gestern');
      return fmt(d, 'medium');
    } catch {
      return iso;
    }
  };

  const reportUpload = (failedCount, total) => {
    if (failedCount === 0) return;
    if (failedCount === total) toast.error(t('calendar.uploadFailed', { message: `${failedCount}/${total}` }));
    else toast.error(t('calendar.uploadPartlyFailed', { n: failedCount }));
  };

  // Send: upload the picked photos first, then hand text + records over.
  const canSend = !!commentDraft.trim() || pending.length > 0;
  const send = async () => {
    if (!canSend || uploading) return;
    let records = [];
    if (pending.length) {
      setUploading(true);
      try {
        const { attachments: up, failedCount } = await uploadEventPhotos(folder, ev.id, pending);
        reportUpload(failedCount, pending.length);
        records = up;
        if (up.length === 0 && !commentDraft.trim()) return;
      } finally {
        setUploading(false);
      }
    }
    onAddComment(records);
    setPending([]);
    inputRef.current?.focus();
  };

  const addEventPhotos = async (files) => {
    if (!files.length) return;
    setAttaching(true);
    try {
      const { attachments: up, failedCount } = await uploadEventPhotos(folder, ev.id, files);
      reportUpload(failedCount, files.length);
      if (up.length) onSetAttachments(ev.id, [...attachments, ...up]);
    } finally {
      setAttaching(false);
    }
  };

  const removeEventPhoto = async (att) => {
    if (!(await confirm({ message: t('calendar.removePhotoConfirm'), danger: true }))) return false;
    onSetAttachments(ev.id, attachments.filter((a) => a.path !== att.path));
    deleteEventPhotos([att.path]);
    return true;
  };

  const thumb = (att, size, onClick) => (
    <button
      type="button"
      key={att.path}
      onClick={onClick}
      className="relative flex-shrink-0 rounded-lg overflow-hidden bg-black/10"
      style={{ width: size, height: size }}
      aria-label={att.name || t('calendar.photo')}
    >
      {signed[att.path] ? (
        <img src={signed[att.path]} alt="" className="w-full h-full object-cover" loading="lazy" />
      ) : (
        <div className="w-full h-full animate-pulse bg-gray-200 dark:bg-gray-700" />
      )}
    </button>
  );

  // Height follows the visible part of the screen so the keyboard shrinks
  // the sheet instead of pushing it; desktop keeps the centred card.
  const sheetStyle =
    viewport.height != null
      ? { height: viewport.height, transform: viewport.top ? `translateY(${viewport.top}px)` : undefined }
      : { height: '100dvh' };

  return (
    <div
      className="fixed inset-0 z-[55] bg-black/40 flex items-start sm:items-center justify-center sm:p-4"
      style={{ height: '100dvh' }}
      onClick={() => onClose()}
    >
      <div
        className="bg-white dark:bg-gray-900 w-full sm:max-w-lg sm:rounded-2xl shadow-2xl flex flex-col sm:!h-auto sm:max-h-[90vh] sm:!transform-none overflow-hidden"
        style={sheetStyle}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header (label-tinted), fixed while the thread scrolls. The sheet
            is full-height on the phone, so the toolbar starts below the
            status bar; the tint still runs up behind it. Folded, the toolbar
            row carries the faces, title and time itself. */}
        <div
          className="flex-shrink-0"
          style={{
            backgroundColor: `${color}1a`,
            paddingTop: 'calc(env(safe-area-inset-top, 0px) + 0.5rem)'
          }}
        >
          <div className="flex items-center gap-1 px-3">
            <button
              onClick={() => onClose()}
              className="p-1.5 -ml-1 rounded-full hover:bg-black/5 dark:hover:bg-white/5 flex-shrink-0"
              aria-label={t('common.back')}
              style={{ color }}
            >
              <ArrowLeft size={22} />
            </button>
            {collapsed ? (
              <button
                type="button"
                onClick={() => setManualCollapsed(false)}
                className="flex-1 min-w-0 flex items-center gap-2 text-left py-1"
                aria-label={t('calendar.expandHeader')}
              >
                {ids.length > 0 && (
                  <div className="flex items-center -space-x-1.5 flex-shrink-0">
                    {ids.slice(0, 3).map((uid) => (
                      <div key={uid} className="ring-2 rounded-full" style={{ ['--tw-ring-color']: `${color}33` }}>
                        {renderCreatorAvatar(uid, 22)}
                      </div>
                    ))}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-bold leading-tight truncate" style={{ color }}>
                    {ev.title}
                  </div>
                  <div className="text-xs text-gray-700 dark:text-gray-300 truncate">{compactWhen}</div>
                </div>
                <ChevronDown size={16} className="flex-shrink-0 opacity-70" style={{ color }} />
              </button>
            ) : (
              <div className="flex-1" />
            )}
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={() => {
                  onClose();
                  onEdit(ev, occurrenceDate);
                }}
                className="p-1.5 rounded-full hover:bg-black/5 dark:hover:bg-white/5"
                aria-label={t('common.edit')}
                style={{ color }}
              >
                <Pencil size={20} />
              </button>
              <button
                onClick={async () => {
                  // Recurring events get the 3-option dialog (this
                  // occurrence / this and future / whole series) —
                  // a plain confirm here silently deleted the whole
                  // series including past occurrences.
                  if (ev.recurrence && ev.recurrence.freq) {
                    onRequestRecurringDelete({
                      baseId: ev.id,
                      occurrenceDate: occurrenceDate || ev.date
                    });
                    onClose();
                    return;
                  }
                  if (await confirm({ message: t('calendar.deleteEventConfirm'), danger: true })) {
                    commitAllEvents(events.filter((e) => e.id !== ev.id));
                    onClose();
                  }
                }}
                className="p-1.5 rounded-full hover:bg-black/5 dark:hover:bg-white/5"
                aria-label={t('common.delete')}
                style={{ color }}
              >
                <Trash2 size={20} />
              </button>
            </div>
          </div>

          {collapsed ? (
            <div className="h-2" />
          ) : (
            <div className="px-4 pb-1">
              {/* Avatars */}
              {ids.length > 0 && (
                <div className="mt-1 flex items-center justify-center -space-x-2">
                  {ids.slice(0, 6).map((uid) => (
                    <div key={uid} className="ring-2 rounded-full" style={{ ['--tw-ring-color']: `${color}33` }}>
                      {renderCreatorAvatar(uid, 36)}
                    </div>
                  ))}
                </div>
              )}

              {/* Title */}
              <h2 className="mt-2 text-center text-2xl font-bold leading-tight" style={{ color }}>
                {ev.title}
              </h2>

              {/* Date / time */}
              <div className="mt-2 flex items-center justify-center gap-3 text-sm">
                <div className="text-center">
                  <div className="font-semibold text-gray-800 dark:text-gray-100">{fmtDate(ev.date)}</div>
                  {!ev.allDay && ev.startTime && (
                    <div className="text-2xl font-bold leading-none mt-0.5" style={{ color }}>
                      {fmtTime(ev.startTime)}
                    </div>
                  )}
                </div>
                <ChevronRight size={20} style={{ color }} />
                <div className="text-center">
                  <div className="font-semibold text-gray-800 dark:text-gray-100">
                    {fmtDate(ev.endDate || ev.date)}
                  </div>
                  {!ev.allDay && ev.endTime && (
                    <div className="text-2xl font-bold leading-none mt-0.5" style={{ color }}>
                      {fmtTime(ev.endTime)}
                    </div>
                  )}
                  {ev.allDay && sameDay && (
                    <div className="text-xs text-gray-500 mt-0.5">{t('calendar.allDay')}</div>
                  )}
                </div>
              </div>

              {ev.notes && (
                <p className="mt-2 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap text-center">
                  {ev.notes}
                </p>
              )}

              {/* Photos on the event itself, with a tile to add more. */}
              <div className="mt-3 flex items-center gap-2 overflow-x-auto no-scrollbar -mx-1 px-1">
                {attachments.map((att) =>
                  thumb(att, 56, () => setLightbox({ path: att.path, onRemove: () => removeEventPhoto(att) }))
                )}
                <label
                  className="flex-shrink-0 w-14 h-14 rounded-lg border border-dashed flex items-center justify-center cursor-pointer hover:bg-black/5 dark:hover:bg-white/5"
                  style={{ borderColor: `${color}66`, color }}
                  title={t('calendar.addPhoto')}
                >
                  {attaching ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />}
                  <input
                    ref={eventFileRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    disabled={attaching}
                    onChange={(e) => {
                      addEventPhotos(Array.from(e.target.files || []));
                      e.target.value = '';
                    }}
                  />
                </label>
                {attachments.length === 0 && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">{t('calendar.addPhoto')}</span>
                )}
              </div>

              <button
                type="button"
                onClick={() => setManualCollapsed(true)}
                className="mt-1 mx-auto flex items-center justify-center w-10 h-6 rounded-full opacity-60 hover:opacity-100"
                aria-label={t('calendar.collapseHeader')}
                style={{ color }}
              >
                <ChevronUp size={18} />
              </button>
            </div>
          )}
        </div>

        {/* Activity timeline — the only part that scrolls. */}
        <div ref={threadRef} className="flex-1 min-h-0 overflow-y-auto px-4 py-4 bg-white dark:bg-gray-900">
          {timeline.length === 0 ? (
            <div className="text-center text-xs text-gray-400 dark:text-gray-500 py-6">
              {t('calendar.noActivity')}
            </div>
          ) : (
            <div className="space-y-3">
              {timeline.map((item, idx) => {
                const prev = timeline[idx - 1];
                const showDateHeader =
                  !prev ||
                  format(parseISO(item.at), 'yyyy-MM-dd') !== format(parseISO(prev.at), 'yyyy-MM-dd');
                const m = membersById[item.userId];
                const name = m?.displayName || t('common.unknown');
                const isMe = user && item.userId === user.id;
                const photos = Array.isArray(item.attachments) ? item.attachments : [];
                return (
                  <div key={item.id}>
                    {showDateHeader && (
                      <div className="flex items-center gap-2 my-3 text-[11px] uppercase tracking-wide text-gray-400">
                        <div className="flex-1 border-t border-gray-200 dark:border-gray-800" />
                        <span>{fmtRelative(item.at)}</span>
                        <div className="flex-1 border-t border-gray-200 dark:border-gray-800" />
                      </div>
                    )}

                    {item.kind === 'audit' ? (
                      <div className="flex items-center justify-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                        {renderCreatorAvatar(item.userId, 18)}
                        <span>{auditSummary(item)}</span>
                      </div>
                    ) : (
                      <div className={`flex items-end gap-2 ${isMe ? 'flex-row-reverse' : ''}`}>
                        {!isMe && renderCreatorAvatar(item.userId, 28)}
                        <div
                          className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${
                            isMe
                              ? 'bg-primary-600 text-white'
                              : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100'
                          }`}
                          style={isMe ? { backgroundColor: color } : undefined}
                        >
                          {!isMe && (
                            <div className="text-[11px] font-semibold opacity-70 mb-0.5">{name}</div>
                          )}
                          {photos.length > 0 && (
                            <div className={`flex flex-wrap gap-1 ${item.text ? 'mb-1.5' : ''}`}>
                              {photos.map((att) =>
                                thumb(att, photos.length === 1 ? 180 : 88, () => setLightbox({ path: att.path }))
                              )}
                            </div>
                          )}
                          {item.text && (
                            <div className="whitespace-pre-wrap break-words">{item.text}</div>
                          )}
                          <div className={`text-[10px] mt-1 ${isMe ? 'text-white/70' : 'text-gray-500'}`}>
                            {format(parseISO(item.at), 'HH:mm')}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Composer: one pill with the text field and, on the right, the
            photo button — which turns into Send as soon as there is
            something to send. Picked photos preview above the pill. */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            send();
          }}
          className="flex-shrink-0 border-t border-gray-200 dark:border-gray-800 px-3 pt-2 bg-white dark:bg-gray-900"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 0.5rem)' }}
        >
          {previews.length > 0 && (
            <div className="flex items-center gap-2 pb-2 overflow-x-auto no-scrollbar">
              {previews.map((p, i) => (
                <div key={p.url} className="relative flex-shrink-0">
                  <img src={p.url} alt="" className="w-16 h-16 rounded-lg object-cover" />
                  <button
                    type="button"
                    onClick={() => setPending((list) => list.filter((_, j) => j !== i))}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-gray-900 text-white flex items-center justify-center shadow"
                    aria-label={t('common.delete')}
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-full pl-4 pr-1 py-1">
            <input
              ref={inputRef}
              type="text"
              value={commentDraft}
              onChange={(e) => setCommentDraft(e.target.value)}
              onFocus={() => {
                const el = threadRef.current;
                if (el) setTimeout(() => (el.scrollTop = el.scrollHeight), 50);
              }}
              placeholder={t('calendar.commentPlaceholder')}
              enterKeyHint="send"
              // The global dark/native input rules paint a box; the pill is the field here.
              className="flex-1 min-w-0 !bg-transparent !border-0 !shadow-none !rounded-none py-1.5 text-base sm:text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-0"
            />
            {canSend ? (
              <button
                type="submit"
                disabled={uploading}
                className="w-9 h-9 rounded-full text-white flex items-center justify-center disabled:opacity-60"
                style={{ backgroundColor: color }}
                aria-label={t('calendar.send')}
              >
                {uploading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
              </button>
            ) : (
              <label
                className="w-9 h-9 rounded-full flex items-center justify-center cursor-pointer hover:bg-black/5 dark:hover:bg-white/10"
                style={{ color }}
                title={t('calendar.addPhoto')}
              >
                <ImagePlus size={20} />
                <input
                  ref={commentFileRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    if (files.length) setPending((list) => [...list, ...files]);
                    e.target.value = '';
                  }}
                />
              </label>
            )}
          </div>
        </form>
      </div>

      {/* Lightbox: one photo, full screen; the event's own photos can be
          removed from here. */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[60] bg-black flex items-center justify-center"
          onClick={(e) => {
            e.stopPropagation();
            setLightbox(null);
          }}
        >
          {signed[lightbox.path] && (
            <img src={signed[lightbox.path]} alt="" className="max-w-full max-h-full object-contain" />
          )}
          <div
            className="absolute left-0 right-0 flex items-center justify-between px-4"
            style={{ top: 'calc(env(safe-area-inset-top, 0px) + 0.5rem)' }}
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setLightbox(null);
              }}
              className="p-2 rounded-full bg-white/15 text-white"
              aria-label={t('common.close')}
            >
              <X size={20} />
            </button>
            {lightbox.onRemove && (
              <button
                type="button"
                onClick={async (e) => {
                  e.stopPropagation();
                  if (await lightbox.onRemove()) setLightbox(null);
                }}
                className="p-2 rounded-full bg-white/15 text-white"
                aria-label={t('common.delete')}
              >
                <Trash2 size={20} />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EventDetail;
